/** Hafif markdown renderer — bold, italik, kod, tablo, başlık, liste, bağlantı */

function renderInline(text) {
  // Escape HTML
  let s = String(text || '')
  // Bağlantılar — [text](url)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-primary-600 hover:underline">$1</a>')
  // Bold **text**
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>')
  // Italic *text*
  s = s.replace(/(^|[\s(])\*([^*]+)\*(?=[\s.,!?)]|$)/g, '$1<em class="italic">$2</em>')
  // Inline code `code`
  s = s.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-gray-100 text-violet-700 rounded text-[11px]">$1</code>')
  return s
}

function parseTable(lines, i) {
  // lines[i] = "| col | col |", lines[i+1] = "|---|---|", lines[i+2..] = data rows
  const header = lines[i].split('|').slice(1, -1).map((x) => x.trim())
  if (!lines[i + 1] || !/^\|[\s|:-]+\|$/.test(lines[i + 1].trim())) return null
  const rows = []
  let j = i + 2
  while (j < lines.length && lines[j].trim().startsWith('|')) {
    rows.push(lines[j].split('|').slice(1, -1).map((x) => x.trim()))
    j += 1
  }
  return { header, rows, nextIdx: j }
}

// Metindeki markdown tablolarını düz veri olarak döndürür (asistan yanıtını Excel'e aktarmak için).
// Hücredeki biçim işaretleri (**kalın**, `kod`, [bağlantı](url)) atılır.
const _plainCell = (c) => String(c || '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/`([^`]+)`/g, '$1')
  .replace(/(^|[\s(])\*([^*]+)\*(?=[\s.,!?)]|$)/g, '$1$2')
  .trim()

export function extractTables(text) {
  const lines = String(text || '').split('\n')
  const out = []
  let i = 0
  while (i < lines.length) {
    const t = lines[i].trim()
    if (t.startsWith('|') && t.endsWith('|') && lines[i + 1] && /^\|[\s|:-]+\|$/.test(lines[i + 1].trim())) {
      const tb = parseTable(lines, i)
      if (tb) {
        out.push({ header: tb.header.map(_plainCell), rows: tb.rows.map((r) => r.map(_plainCell)) })
        i = tb.nextIdx
        continue
      }
    }
    i += 1
  }
  return out
}

export default function MiniMarkdown({ text }) {
  const lines = String(text || '').split('\n')
  const out = []
  let i = 0
  let key = 0
  let listBuf = null

  const flushList = () => {
    if (listBuf) {
      out.push(
        <ul key={`l${key++}`} className="list-disc list-inside space-y-0.5 my-1.5">
          {listBuf.map((x, ii) => (
            <li key={ii} dangerouslySetInnerHTML={{ __html: renderInline(x) }} />
          ))}
        </ul>
      )
      listBuf = null
    }
  }

  while (i < lines.length) {
    const ln = lines[i]
    const trimmed = ln.trim()

    // Table
    if (trimmed.startsWith('|') && trimmed.endsWith('|') && lines[i + 1] && /^\|[\s|:-]+\|$/.test(lines[i + 1].trim())) {
      flushList()
      const t = parseTable(lines, i)
      if (t) {
        out.push(
          <div key={`t${key++}`} className="overflow-x-auto my-2">
            <table className="min-w-full text-xs border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  {t.header.map((h, hi) => (
                    <th key={hi} className="border border-gray-200 px-2 py-1 text-left font-semibold"
                        dangerouslySetInnerHTML={{ __html: renderInline(h) }} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.rows.map((r, ri) => (
                  <tr key={ri} className={ri % 2 ? 'bg-gray-50/50' : ''}>
                    {r.map((c, ci) => (
                      <td key={ci} className="border border-gray-200 px-2 py-1"
                          dangerouslySetInnerHTML={{ __html: renderInline(c) }} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
        i = t.nextIdx
        continue
      }
    }

    // Header ##
    if (trimmed.startsWith('## ')) {
      flushList()
      out.push(
        <div key={`h${key++}`} className="font-semibold text-gray-900 mt-2 mb-1"
             dangerouslySetInnerHTML={{ __html: renderInline(trimmed.slice(3)) }} />
      )
      i += 1; continue
    }
    if (trimmed.startsWith('# ')) {
      flushList()
      out.push(
        <div key={`h${key++}`} className="font-bold text-base text-gray-900 mt-2 mb-1"
             dangerouslySetInnerHTML={{ __html: renderInline(trimmed.slice(2)) }} />
      )
      i += 1; continue
    }

    // Bullet list
    if (/^[-*•]\s/.test(trimmed)) {
      if (!listBuf) listBuf = []
      listBuf.push(trimmed.replace(/^[-*•]\s+/, ''))
      i += 1; continue
    }

    // Blank line
    if (!trimmed) {
      flushList()
      i += 1; continue
    }

    // Paragraph
    flushList()
    out.push(
      <p key={`p${key++}`} className="my-1"
         dangerouslySetInnerHTML={{ __html: renderInline(ln) }} />
    )
    i += 1
  }
  flushList()
  return <>{out}</>
}
